import { Tooltip } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShareForwardedRef } from '../../../../hooks/refs.hooks';
import { useAppDispatch } from '../../../../redux/hooks';
import { changeEventLength } from '../../../../redux/trip/trip.operations';
import { EventID, TripCard, TripEvent, TripID } from '../../../../redux/trip/trip.types';
import { EItineraryEvents } from '../../../../types/Itinerary';
import { getOpenStatus } from '../../../../utils/Card';
import { textColorForBg } from '../../../../utils/color.utils';
import { getNotesPreviewText, isNotesEmpty } from '../../../../utils/slate.utils';
import CardDetailsModal from '../../../CardDetailsModal/CardDetailsModal';
import './ItineraryEvent.scss';

const GRID_GAP = Number(EItineraryEvents.gridGap);
const CONTAINER_PADDING = Number(EItineraryEvents.containerPadding);
const NAME_MAX_LENGTH = 70;

export interface ItineraryEventProps {
  tripId: TripID;
  event: TripEvent;
  card: TripCard;
  emoji: string;
  color: string;
  listLabel?: string;
  eventOrder?: EventID[];
  canEdit?: boolean;
  eventLength?: number;
  isMultidayEvent?: boolean;
  markerNumber?: number;
}

const ItineraryEvent = forwardRef<HTMLDivElement, ItineraryEventProps>(
  (
    {
      tripId,
      event,
      card,
      emoji,
      color,
      listLabel,
      eventOrder,
      canEdit,
      eventLength,
      isMultidayEvent,
      markerNumber,
    },
    forwardedRef
  ) => {
    // ─── Hooks ───────────────────────────────────────────────────────────────
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    // ─── State & refs ────────────────────────────────────────────────────────
    const wasResizingRef = useRef(false);
    const startWidthRef = useRef<number | undefined>(undefined);
    const ref = useShareForwardedRef<HTMLDivElement>(forwardedRef);

    // ─── Derived values ──────────────────────────────────────────────────────
    const open = searchParams.get('card') === card.id;

    // ─── Callbacks ───────────────────────────────────────────────────────────
    const handleOpen = useCallback(() => {
      if (wasResizingRef.current) {
        wasResizingRef.current = false;
        return;
      }
      if (event.id !== card.id) {
        setSearchParams((prev) => {
          prev.set('card', card.id);
          return prev;
        });
      }
    }, [event.id, card.id, setSearchParams]);

    const handleClose = useCallback(() => {
      setSearchParams((prev) => {
        prev.delete('card');
        return prev;
      });
    }, [setSearchParams]);

    // ─── Drag and drop ───────────────────────────────────────────────────────
    // Drag is handled by SortableItineraryEvent (useSortable wrapper).

    // ─── Resize handlers ─────────────────────────────────────────────────────
    const dispatchLength = useCallback(
      (eventId: string, length: number | undefined) => {
        dispatch(changeEventLength({ tripId, eventId, eventLength: length }));
      },
      [dispatch, tripId]
    );

    const commitResize = useCallback(
      (target: HTMLElement, startWidth: number, eventId: string) => {
        const currentWidth = target.clientWidth;
        const grew = currentWidth >= startWidth;

        if (isMultidayEvent && eventLength) {
          const chunkSize = Math.floor(startWidth / eventLength);
          const daysDelta = grew
            ? Math.ceil((currentWidth - startWidth) / chunkSize)
            : -Math.floor((startWidth - currentWidth) / chunkSize);
          const newLength = eventLength + daysDelta;

          if (!grew && !-daysDelta) {
            target.style.width = '100%';
          }
          dispatchLength(eventId, newLength <= 1 ? undefined : newLength);
        } else {
          if (!grew) {
            target.style.width = '100%';
          }
          const newLength = Math.ceil(
            Math.ceil(
              currentWidth -
                Math.ceil(currentWidth / startWidth) * (GRID_GAP + CONTAINER_PADDING * 2)
            ) / startWidth
          );
          dispatchLength(eventId, newLength);
        }
      },
      [isMultidayEvent, eventLength, dispatchLength]
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!event.id) {
          return;
        }
        const resizable = document.getElementById(event.id);
        if (resizable) {
          resizable.style.width = `${resizable.offsetWidth + e.movementX}px`;
        }
      },
      [event.id]
    );

    const handleResizeCommit = useCallback(() => {
      wasResizingRef.current = true;
      if (!event.id) {
        return;
      }
      const target = document.getElementById(event.id);
      if (target instanceof HTMLElement && startWidthRef.current) {
        commitResize(target, startWidthRef.current, event.id);
      }
      document.removeEventListener('mouseup', handleResizeCommit);
      document.removeEventListener('mousemove', handleMouseMove);
    }, [event.id, commitResize, handleMouseMove]);

    const handleResizeStart = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        document.addEventListener('mouseup', handleResizeCommit);
        document.addEventListener('mousemove', handleMouseMove);
      },
      [handleResizeCommit, handleMouseMove]
    );

    // ─── Effects ─────────────────────────────────────────────────────────────
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when eventLength changes
    useEffect(() => {
      if (!event.id) {
        return;
      }
      const target = document.getElementById(event.id);
      if (target) {
        target.style.width = '100%';
        startWidthRef.current = target.clientWidth;
      }
    }, [event.id, eventLength]);

    // ─── Render data ─────────────────────────────────────────────────────────
    const displayName = (card.customName ?? card.name) || '';
    const truncatedName = useMemo(
      () =>
        displayName.length > NAME_MAX_LENGTH
          ? `${displayName.substring(0, NAME_MAX_LENGTH)}...`
          : displayName,
      [displayName]
    );
    const textColor = useMemo(() => textColorForBg(color), [color]);
    const openStatus = useMemo(() => getOpenStatus(card.openingHours), [card.openingHours]);
    const notesPreview = useMemo(
      () => (!isNotesEmpty(card) && card.notes ? getNotesPreviewText(card.notes) : null),
      [card]
    );
    const containerClass = useMemo(
      () =>
        ['itinerary-event-container', event.isConfirmed ? 'confirmed' : 'unconfirmed']
          .filter(Boolean)
          .join(' '),
      [event.isConfirmed]
    );

    return (
      <>
        <div
          id={event.id}
          ref={ref}
          className={containerClass}
          style={{ background: color }}
          onClick={handleOpen}
        >
          {markerNumber !== undefined && (
            <span
              className='itinerary-event-marker-num'
              style={{ background: 'rgba(255,255,255,0.92)', color: '#141414' }}
            >
              {markerNumber}
            </span>
          )}
          <Tooltip
            title={listLabel || ''}
            placement='top'
            arrow
            enterDelay={400}
            disableInteractive
          >
            <span className='itinerary-event-icon'>{emoji}</span>
          </Tooltip>
          <div
            className='itinerary-event-body'
            style={{ color: textColor }}
          >
            <span className='itinerary-event-title'>{truncatedName}</span>
            {notesPreview && <p className='itinerary-event-notes-preview'>{notesPreview}</p>}
            {openStatus && (
              <span className='itinerary-event-open-status'>
                {openStatus.isOpen ? (
                  openStatus.label
                ) : (
                  <>
                    <span className='itinerary-event-open-status__closed'>Closed</span>
                    {openStatus.nextOpenTime && ` · Opens ${openStatus.nextOpenTime}`}
                  </>
                )}
              </span>
            )}
          </div>
          <div
            className='itinerary-event-resizer'
            draggable='true'
            onMouseDown={handleResizeStart}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        {open && (
          <CardDetailsModal
            card={card}
            tripId={tripId}
            open={open}
            canEdit={canEdit}
            listLabel={listLabel}
            handleClose={handleClose}
          />
        )}
      </>
    );
  }
);

ItineraryEvent.displayName = 'ItineraryEvent';
export default ItineraryEvent;
